import { TicketIcon, XIcon } from '@phosphor-icons/react';
import { Skeleton } from 'antd';
import { useEffect } from 'react';

import { UserAvatar } from '@/components/common/user-avatar';
import { Drawer } from '@/components/feedback/drawer';
import { ApiError } from '@/lib/api/errors';
import { customerAvatar } from '@/lib/avatar';

import {
  formatCoordinates,
  formatInstallationAddress,
  formatInstallationDateTime,
} from '../format';
import { useInstallation } from '../hooks';
import type { InstallationDetail, InstallationDetailDevice } from '../types';

import styles from './installation-detail-drawer.module.scss';

export type InstallationDetailDrawerProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  /** The requested id could not be shown — missing, or not visible to this admin. Lets the host drop
   *  back to its plain list (and clear its URL) instead of a drawer that never resolves. */
  onUnavailable?: () => void;
  onApprove: (detail: InstallationDetail) => void;
  onReject: (detail: InstallationDetail) => void;
  /** Navigates to the full-page Mapped Devices route (ADR-0018). */
  onDeviceClick?: (device: InstallationDetailDevice) => void;
};

/** Cream header band cluster: ticket-icon badge + title + order-id subtitle (Figma 3182:252662). */
function HeaderTitle({ orderNumber }: { orderNumber?: string }) {
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerText}>
        <span className={styles.headerHeading}>Installation Request</span>
        {orderNumber ? <span className={styles.headerSubtitle}>{orderNumber}</span> : null}
      </span>
    </span>
  );
}

type DetailBodyProps = {
  detail?: InstallationDetail;
  isPending: boolean;
  onDeviceClick?: (device: InstallationDetailDevice) => void;
};

function DetailBody({ detail, isPending, onDeviceClick }: DetailBodyProps) {
  if (isPending || !detail) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <>
      <div className={styles.infoCard}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Name</span>
          <span className={styles.infoValue}>
            <UserAvatar
              name={detail.customerName}
              src={customerAvatar(detail.customerName)}
              label={detail.customerName}
              size={18}
            />
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Phone Number</span>
          <span className={styles.infoValue}>{detail.phone}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Address</span>
          <span className={styles.infoValue}>{formatInstallationAddress(detail.address)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Order Date & Time</span>
          <span className={styles.infoValue}>{formatInstallationDateTime(detail.orderDate)}</span>
        </div>
      </div>

      <h3 className={styles.sectionTitle}>Device</h3>
      <ul className={styles.devices}>
        {detail.devices.map((device) => (
          <li key={device.id}>
            <button type="button" className={styles.device} onClick={() => onDeviceClick?.(device)}>
              {/* Figma draws a product thumbnail ahead of the name (ADO 119717, QA reopen). The
                  empty `<span>` keeps the box when the backend sends no `imageUrl`, so the card
                  does not reflow once the field lands — the `<OrderSummaryCard>` idiom. */}
              {device.imageUrl ? (
                <img className={styles.thumb} src={device.imageUrl} alt="" data-device-thumb />
              ) : (
                <span className={styles.thumb} data-device-thumb aria-hidden />
              )}
              <span className={styles.deviceInfo}>
                <span className={styles.deviceName}>{device.deviceName}</span>
                <span className={styles.deviceSubtitle}>
                  {formatCoordinates(device.latitude, device.longitude)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      {detail.status === 'rejected' && detail.rejectionReason ? (
        <div className={styles.rejection} role="alert">
          <span className={styles.rejectionHeading}>
            <XIcon size={18} aria-hidden />
            Request Rejected
          </span>
          <p className={styles.rejectionValue}>{detail.rejectionReason}</p>
        </div>
      ) : null}
    </>
  );
}

/** Read-only installation-request details (ticket 06, ADR-0018): ticket-header + order metadata
 *  card, device-card list (clickable → map), and — when Rejected — the actual rejection reason in
 *  a red banner; Approve/Reject CTAs only when Open. `useInstallation` is `id`-gated, so it stays
 *  idle while closed (id=''). */
export function InstallationDetailDrawer({
  open,
  id,
  onClose,
  onUnavailable,
  onApprove,
  onReject,
  onDeviceClick,
}: InstallationDetailDrawerProps) {
  const { data: detail, isPending, error } = useInstallation(open ? id : '');

  // Only "cannot be shown" closes the drawer: a missing id (404) or one this admin may not see
  // (403). A transient 5xx is left alone rather than silently destroying the id in the host's URL.
  const unavailable = error instanceof ApiError && (error.status === 404 || error.status === 403);

  // No loop even with an inline callback: the host's handler clears the id, `open` goes false, and
  // the condition stops holding.
  useEffect(() => {
    if (open && unavailable) onUnavailable?.();
  }, [open, unavailable, onUnavailable]);
  // Approve/Reject live in the footer (right-aligned, no Cancel) and only when the request is
  // Open; the other statuses show a footer-less read-only panel.
  const actionable = detail?.status === 'open' ? detail : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={549}
      title={<HeaderTitle orderNumber={open ? detail?.orderNumber : undefined} />}
      hideFooter={!actionable}
      primaryText={actionable ? 'Approve' : undefined}
      onPrimary={actionable ? () => onApprove(actionable) : undefined}
      cancelText={actionable ? 'Reject' : undefined}
      onCancel={actionable ? () => onReject(actionable) : undefined}
    >
      {open ? (
        <DetailBody detail={detail} isPending={isPending} onDeviceClick={onDeviceClick} />
      ) : null}
    </Drawer>
  );
}
